import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../Assets/logo1.png';
import { useTranslation } from "react-i18next";

const Preloader = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Dismiss as soon as page is ready, max 1200ms
    const maxTimer = setTimeout(() => setLoading(false), 1200);

    const onLoad = () => {
      clearTimeout(maxTimer);
      // Small buffer so exit animation looks smooth
      setTimeout(() => setLoading(false), 200);
    };

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
    }

    return () => {
      clearTimeout(maxTimer);
      window.removeEventListener('load', onLoad);
    };
  }, []);

  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          key="preloader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } }}
          className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-[#070707] overflow-hidden"
        >
          {/* Subtle Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[600px] max-h-[600px] bg-orange-main/5 rounded-full blur-[100px] pointer-events-none" />

          {/* Logo Animation */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, filter: "blur(10px)" }}
            animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="w-48 md:w-64 relative z-10"
          >
            <motion.img 
              src={logo} 
              alt={t('preloader_alt')} 
              className="w-full h-auto object-contain drop-shadow-2xl"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Loading Text */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-12 overflow-hidden relative z-10"
          >
            <motion.p
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
              className="text-white/40 text-[10px] md:text-xs font-medium tracking-[0.4em] uppercase"
            >
              {t('preloader_text')}
            </motion.p>
          </motion.div>

          {/* Sleek Bottom Loading Line */}
          <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5">
            <motion.div
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.2, ease: "circInOut" }}
              className="h-full bg-orange-main shadow-[0_0_15px_rgba(240,173,78,0.8)]"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;

